import React from "react";
import { ChevronDown, Cloud, Shield, Cpu, Settings } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

const sectionIcon = (iconName) => {
  const cls = "w-4 h-4";
  switch (iconName) {
    case "cloud": return <Cloud className={cls} />;
    case "shield": return <Shield className={cls} />;
    case "cpu": return <Cpu className={cls} />;
    default: return <Settings className={cls} />;
  }
};

const decodeHtmlEntities = (str) => {
  if (typeof str !== "string") return str;
  if (!/&(amp|lt|gt|quot|#39|#\d+);/.test(str)) return str;
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

const Field = ({ label, value, mono, testId }) => (
  <div
    className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0"
    style={{ borderColor: "var(--border-color)" }}
    data-testid={testId}
  >
    <span className="hud-label shrink-0 pt-0.5">{label}</span>
    <span
      className={`text-sm ${mono ? "font-mono-hud" : "font-body"} text-right min-w-0 break-all`}
      style={{ color: "var(--text-primary)" }}
    >
      {decodeHtmlEntities(value) || "—"}
    </span>
  </div>
);

export const ServerDetailsAccordion = ({ details }) => {
  const [open, setOpen] = React.useState(false);
  const sections = details?.sections || [];
  if (!sections.length) return null;
  const sourceLabel = (details?.source || "").toUpperCase();

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid="details-accordion">
      <div
        className="tac-card"
        style={{ background: "var(--bg-surface)" }}
      >
        <span className="corner-tr" />
        <span className="corner-bl" />
        <CollapsibleTrigger
          className="w-full flex items-center justify-between p-4 transition-colors hover:bg-[color-mix(in_srgb,var(--primary-accent)_6%,transparent)]"
          data-testid="details-accordion-trigger"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Settings
              className="w-4 h-4 shrink-0"
              style={{ color: "var(--primary-accent)" }}
            />
            <div className="text-left min-w-0">
              <div className="hud-label">SERVER DETAILS</div>
              <div
                className="font-head text-sm uppercase font-bold tracking-wide truncate"
                style={{ color: "var(--text-primary)" }}
              >
                {sourceLabel ? `${sourceLabel} · ` : ""}Extended Info
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className="font-mono-hud text-[10px] tracking-widest hidden sm:inline"
              style={{ color: "var(--text-dim)" }}
            >
              {open ? "COLLAPSE" : "EXPAND"}
            </span>
            <ChevronDown
              className="w-5 h-5 transition-transform"
              style={{
                color: "var(--primary-accent)",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 overflow-hidden"
          data-testid="details-accordion-content"
        >
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 pt-0 border-t"
            style={{ borderColor: "var(--border-color)" }}
          >
            {sections.map((sec) => (
              <div
                key={sec.title}
                className="border p-3"
                style={{ borderColor: "var(--border-color)", background: "var(--bg-main)", borderRadius: 2 }}
                data-testid={`detail-section-${sec.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              >
                <div className="flex items-center gap-2 mb-2 pb-2 border-b"
                  style={{ borderColor: "var(--border-color)" }}>
                  <span style={{ color: "var(--primary-accent)" }}>{sectionIcon(sec.icon)}</span>
                  <span
                    className="font-head uppercase font-bold text-xs tracking-widest"
                    style={{ color: "var(--primary-accent)" }}
                  >
                    {sec.title}
                  </span>
                </div>
                <div>
                  {(sec.fields || []).map((f, i) => (
                    <Field
                      key={`${sec.title}-${i}-${f.label}`}
                      label={f.label}
                      value={f.value}
                      mono={f.mono}
                      testId={`detail-field-${f.label.toLowerCase().replace(/\s+/g, "-").replace(/[\/()]/g, "")}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default ServerDetailsAccordion;
